import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type IStarsProps = Immutable<{
    rating: string | number | null | undefined;
    /** Adds the "N out of 5 stars" screen-reader text. */
    srLabel?: boolean;
    overlayClassName?: string;
}>;

/** The sprite star rating: the rating element (className) and its overlay. */
export const StarRatingStars = (
    props: IStarsProps & Immutable<{ className?: string }>,
): React.JSX.Element => (
    <div
        className={concatClassNames([styles.rating, props.className])}
        data-rating={props.rating ?? ""}
    >
        {props.srLabel ? (
            <span className="ada-screenreader-only">
                {interpolate(t`%(stars)s out of 5 stars`, {
                    stars: props.rating ?? "",
                })}
            </span>
        ) : null}
        <div
            className={concatClassNames([
                styles.overlay,
                props.overlayClassName,
            ])}
        />
    </div>
);

/** The positioning container (className) the sprite rating sits in. */
export const StarRatingContainer = (
    props: Immutable<{
        inverted?: boolean;
        className?: string;
        children: React.ReactNode;
    }>,
): React.JSX.Element => (
    <div
        className={concatClassNames([
            styles.container,
            props.inverted ? styles.inverted : null,
            props.className,
        ])}
    >
        {props.children}
    </div>
);

/** The star rating inside its container: className is the container's,
 * ratingClassName the rating element's. */
export const StarRating = (
    props: IStarsProps &
        Immutable<{
            inverted?: boolean;
            className?: string;
            ratingClassName?: string;
        }>,
): React.JSX.Element => (
    <StarRatingContainer inverted={props.inverted} className={props.className}>
        <StarRatingStars
            rating={props.rating}
            srLabel={props.srLabel}
            className={props.ratingClassName}
            overlayClassName={props.overlayClassName}
        />
    </StarRatingContainer>
);
